import { useState } from 'react';

import { AppIcon, AppIconProps, ColorToken, InfoModal, InfoModalProps } from '@backrs_ui/common-ui';

import { Box, ClickAwayListener, SxProps, Theme, Tooltip } from '@mui/material';
import ReactMarkdown from 'react-markdown';

export type LineInfo =
  | { kind: 'tooltip'; text: string }
  | { kind: 'modal'; modalProps: Omit<InfoModalProps, 'open' | 'onClose'> }
  | { kind: 'click'; onClick: () => void };

type LineInfoIconProps = Readonly<{
  info: LineInfo;
  idPrefix: string;
  iconColor?: ColorToken;
  iconName?: AppIconProps['iconName'];
  sx?: SxProps<Theme>;
}>;

export const LineInfoIcon = ({
  info,
  idPrefix,
  iconColor = 'Brand/500',
  iconName = 'InfoCircle',
  sx,
}: LineInfoIconProps) => {
  const [open, setOpen] = useState(false);

  const trigger = (
    <Box
      id={`${idPrefix}-info`}
      sx={[infoIconSx, ...(Array.isArray(sx) ? sx : [sx])]}
      onClick={() => (info.kind === 'click' ? info.onClick() : setOpen((prev) => !prev))}
    >
      <AppIcon iconName={iconName} diameter="16px" color={iconColor} />
    </Box>
  );

  if (info.kind === 'tooltip') {
    return (
      <ClickAwayListener onClickAway={() => setOpen(false)}>
        <Tooltip
          open={open}
          onClose={() => setOpen(false)}
          title={
            <ReactMarkdown
              components={{ p: ({ ...props }) => <p style={{ margin: 0 }} {...props} /> }}
            >
              {info.text}
            </ReactMarkdown>
          }
          placement="top"
          arrow
          disableFocusListener
          disableHoverListener
          disableTouchListener
        >
          {trigger}
        </Tooltip>
      </ClickAwayListener>
    );
  }

  if (info.kind === 'modal') {
    return (
      <>
        {trigger}
        <InfoModal open={open} onClose={() => setOpen(false)} {...info.modalProps} />
      </>
    );
  }

  return trigger;
};

const infoIconSx: SxProps<Theme> = ({ backrs }) => ({
  marginLeft: backrs.spacing['spacing-sm'],
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
});
